import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Stack,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Fade,
  Collapse,
  useTheme,
  alpha,
  keyframes,
  Skeleton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Autocomplete,
  LinearProgress
} from '@mui/material';
import {
  Notes as NotesIcon,
  Add,
  Edit,
  Delete,
  Search,
  PushPin,
  FilterList,
  MoreVert,
  Star,
  Timeline,
  FormatListBulleted,
  ContentCopy,
  Share,
  Archive,
  Unarchive,
  Flag,
  Sort,
  Group,
  Lock,
  Psychology,
  TipsAndUpdates,
  CheckCircle,
  RadioButtonUnchecked,
  Check,
  AdminPanelSettings
} from '@mui/icons-material';
import { formatDateSafe, toDate } from '../../shared/utils/dateHelpers';
import { formatDistanceToNow } from 'date-fns';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

interface Note {
  id: string;
  content: string;
  created_at: any;
  created_by: string;
  created_by_uid?: string;
  created_by_name?: string;
  is_pinned?: boolean;
  tags?: string[];
  category?: string;
  is_private?: boolean;
  is_management_only?: boolean;
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
    size: number;
  }>;
  mentions?: string[];
  is_important?: boolean;
  color?: string;
  reminder_date?: any;
  last_edited_at?: any;
  last_edited_by?: string;
  last_edited_by_name?: string;
  is_archived?: boolean;
  related_notes?: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
  action_items?: Array<{
    text: string;
    completed: boolean;
    assignee?: string;
  }>;
}

interface NoteCategory {
  id: string;
  name: string;
  icon: React.ReactElement;
  color: string;
  description: string;
}

interface ClientNotesViewProps {
  client: any;
  notes: Note[];
  isLoading: boolean;
  userRole: string | null;
  currentUserId: string;
  currentUserName: string;
  onAddNote: (note: Partial<Note>) => void;
  onEditNote: (noteId: string, updates: Partial<Note>) => void;
  onDeleteNote: (noteId: string) => void;
  onTogglePin: (noteId: string, isPinned: boolean) => void;
  onToggleArchive?: (noteId: string, isArchived: boolean) => void;
  coaches?: Array<{ id: string; name: string }>;
}

const noteCategories: NoteCategory[] = [
  { id: 'general', name: 'General', icon: <NotesIcon />, color: '#2196f3', description: 'General notes and observations' },
  { id: 'progress', name: 'Progress', icon: <TipsAndUpdates />, color: '#4caf50', description: 'Progress updates and milestones' },
  { id: 'health', name: 'Health', icon: <Psychology />, color: '#ff9800', description: 'Health-related observations' },
  { id: 'goals', name: 'Goals', icon: <Flag />, color: '#9c27b0', description: 'Goals and targets' },
  { id: 'concerns', name: 'Concerns', icon: <Flag />, color: '#f44336', description: 'Issues or concerns to address' },
  { id: 'coaching', name: 'Coaching', icon: <Group />, color: '#00bcd4', description: 'Coaching strategies and plans' }
];

const noteColors = [
  { id: 'default', name: 'Default', color: '#ffffff' },
  { id: 'blue', name: 'Blue', color: '#e3f2fd' },
  { id: 'green', name: 'Green', color: '#e8f5e9' },
  { id: 'yellow', name: 'Yellow', color: '#fffde7' },
  { id: 'pink', name: 'Pink', color: '#fce4ec' },
  { id: 'purple', name: 'Purple', color: '#f3e5f5' }
];

const ClientNotesView: React.FC<ClientNotesViewProps> = ({
  client,
  notes,
  isLoading,
  userRole,
  currentUserId,
  currentUserName,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onTogglePin,
  onToggleArchive,
  coaches = []
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'updated'>('newest');
  const [viewMode, setViewMode] = useState<'cards' | 'timeline' | 'compact'>('cards');
  const [showArchived, setShowArchived] = useState(false);
  const [showPrivate, setShowPrivate] = useState(true);
  
  // Dialog states
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [noteCategory, setNoteCategory] = useState<string>('general');
  const [noteTags, setNoteTags] = useState<string[]>([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [noteColor, setNoteColor] = useState<string>('default');
  const [noteIsPrivate, setNoteIsPrivate] = useState(false);
  const [noteIsManagementOnly, setNoteIsManagementOnly] = useState(false);
  const [noteIsImportant, setNoteIsImportant] = useState(false);
  const [noteMentions, setNoteMentions] = useState<string[]>([]);
  const [noteActionItems, setNoteActionItems] = useState<Array<{ text: string; completed: boolean; assignee?: string }>>([]);
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  // Calculate statistics
  const statistics = useMemo(() => {
    const total = notes.length;
    const pinned = notes.filter(n => n.is_pinned).length;
    const important = notes.filter(n => n.is_important).length;
    const privateNotes = notes.filter(n => n.is_private).length;
    const managementOnlyNotes = notes.filter(n => n.is_management_only).length;
    const archived = notes.filter(n => n.is_archived).length;
    const withActionItems = notes.filter(n => n.action_items && n.action_items.length > 0).length;
    
    const categoryCounts = noteCategories.reduce((acc, cat) => {
      acc[cat.id] = notes.filter(n => n.category === cat.id).length;
      return acc;
    }, {} as Record<string, number>);
    
    const recentActivity = notes.filter(n => {
      const date = toDate(n.created_at);
      const daysAgo = date ? (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24) : Infinity;
      return daysAgo <= 7;
    }).length;
    
    return {
      total,
      pinned,
      important,
      privateNotes,
      managementOnlyNotes,
      archived,
      withActionItems,
      categoryCounts,
      recentActivity
    };
  }, [notes]);

  // Extract all unique tags
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    notes.forEach(note => {
      note.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [notes]);

  // Filter and sort notes
  const filteredNotes = useMemo(() => {
    let filtered = [...notes];
    
    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(note => note.category === selectedCategory);
    }
    
    // Tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(note => 
        note.tags?.some(tag => selectedTags.includes(tag))
      );
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(note => 
        note.content.toLowerCase().includes(query) ||
        note.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        note.created_by_name?.toLowerCase().includes(query)
      );
    }
    
    // Archive filter
    if (!showArchived) {
      filtered = filtered.filter(note => !note.is_archived);
    }
    
    // Privacy filters
    // 1. Private notes - only visible to the creator
    filtered = filtered.filter(note => {
      if (note.is_private && note.created_by_uid !== currentUserId) {
        // Admin and managers can see all private notes if showPrivate is true
        return showPrivate && (userRole === 'ADMIN' || userRole === 'MANAGER');
      }
      return true;
    });
    
    // 2. Management-only notes - only visible to managers and admins
    filtered = filtered.filter(note => {
      if (note.is_management_only && userRole !== 'ADMIN' && userRole !== 'MANAGER') {
        return false;
      }
      return true;
    });
    
    // Sort
    filtered.sort((a, b) => {
      const dateA = toDate(sortBy === 'updated' && a.last_edited_at ? a.last_edited_at : a.created_at);
      const dateB = toDate(sortBy === 'updated' && b.last_edited_at ? b.last_edited_at : b.created_at);
      
      if (!dateA || !dateB) return 0;
      
      if (sortBy === 'oldest') {
        return dateA.getTime() - dateB.getTime();
      }
      return dateB.getTime() - dateA.getTime();
    });
    
    // Always show pinned notes first
    const pinned = filtered.filter(n => n.is_pinned);
    const unpinned = filtered.filter(n => !n.is_pinned);
    
    return [...pinned, ...unpinned];
  }, [notes, selectedCategory, selectedTags, searchQuery, showArchived, showPrivate, sortBy, userRole, currentUserId]);

  const handleOpenNoteDialog = (note?: Note) => {
    if (note) {
      setEditingNote(note);
      setNoteContent(note.content);
      setNoteCategory(note.category || 'general');
      setNoteTags(note.tags || []);
      setTagInputValue('');
      setNoteColor(note.color || 'default');
      setNoteIsPrivate(note.is_private || false);
      setNoteIsManagementOnly(note.is_management_only || false);
      setNoteIsImportant(note.is_important || false);
      setNoteMentions(note.mentions || []);
      setNoteActionItems(note.action_items || []);
    } else {
      setEditingNote(null);
      setNoteContent('');
      setNoteCategory('general');
      setNoteTags([]);
      setTagInputValue('');
      setNoteColor('default');
      setNoteIsPrivate(false);
      setNoteIsManagementOnly(false);
      setNoteIsImportant(false);
      setNoteMentions([]);
      setNoteActionItems([]);
    }
    setShowNoteDialog(true);
  };

  const handleSaveNote = () => {
    const noteData: Partial<Note> = {
      content: noteContent,
      category: noteCategory,
      tags: noteTags,
      color: noteColor,
      is_private: noteIsPrivate,
      is_management_only: noteIsManagementOnly,
      is_important: noteIsImportant,
      mentions: noteMentions,
      action_items: noteActionItems
    };
    
    if (editingNote) {
      onEditNote(editingNote.id, noteData);
    } else {
      onAddNote(noteData);
    }
    
    setShowNoteDialog(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, note: Note) => {
    setAnchorEl(event.currentTarget);
    setSelectedNote(note);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNote(null);
  };

  const renderNoteCard = (note: Note, index: number) => {
    const category = noteCategories.find(c => c.id === note.category);
    const bgColor = noteColors.find(c => c.id === note.color)?.color || '#ffffff';
    const isOwner = note.created_by_uid === currentUserId;
    const canEdit = userRole === 'ADMIN' || userRole === 'MANAGER' || isOwner;
    
    return (
      <Fade in timeout={300 * Math.min(index + 1, 5)} key={note.id}>
        <Card
          elevation={note.is_pinned ? 4 : 2}
          sx={{
            mb: 2,
            borderRadius: 2,
            backgroundColor: bgColor,
            borderLeft: `4px solid ${category?.color || theme.palette.divider}`,
            position: 'relative',
            transition: 'all 0.3s ease',
            animation: note.is_pinned ? `${pulse} 2s ease-in-out infinite` : 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[6]
            }
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, pb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {note.is_pinned && (
                  <Tooltip title="Pinned note">
                    <PushPin sx={{ fontSize: 16, color: 'primary.main' }} />
                  </Tooltip>
                )}
                {note.is_important && (
                  <Tooltip title="Important note">
                    <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                  </Tooltip>
                )}
                {note.is_private && (
                  <Tooltip title="Private note (only visible to you)">
                    <Lock sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Tooltip>
                )}
                {note.is_management_only && (
                  <Tooltip title="Management only (visible to managers and admins)">
                    <AdminPanelSettings sx={{ fontSize: 16, color: 'secondary.main' }} />
                  </Tooltip>
                )}
                {category && (
                  <Chip
                    icon={category.icon}
                    label={category.name}
                    size="small"
                    sx={{
                      backgroundColor: alpha(category.color, 0.1),
                      color: category.color,
                      '& .MuiChip-icon': { color: category.color }
                    }}
                  />
                )}
                {note.tags?.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedTags([tag])}
                  />
                ))}
              </Box>
              
              {canEdit && (
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, note)}
                >
                  <MoreVert />
                </IconButton>
              )}
            </Box>
          </Box>
          
          {/* Content */}
          <CardContent>
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                mb: 2,
                color: note.is_archived ? 'text.secondary' : 'text.primary'
              }}
            >
              {note.content}
            </Typography>
            
            {/* Action Items */}
            {note.action_items && note.action_items.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Action Items
                </Typography>
                {note.action_items.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {item.completed ? (
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        textDecoration: item.completed ? 'line-through' : 'none',
                        color: item.completed ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      {item.text}
                      {item.assignee && (
                        <Chip
                          label={item.assignee}
                          size="small"
                          sx={{ ml: 1, height: 20 }}
                        />
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Mentions */}
            {note.mentions && note.mentions.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {note.mentions.map(mention => (
                    <Chip
                      key={mention}
                      label={`@${mention}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            )}
            
            {/* Footer */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {note.created_by_name || note.created_by} • {formatDistanceToNow(toDate(note.created_at) || new Date(), { addSuffix: true })}
                {note.last_edited_at && (
                  <> (edited {formatDistanceToNow(toDate(note.last_edited_at) || new Date(), { addSuffix: true })})</>
                )}
              </Typography>
              
              <Stack direction="row" spacing={1}>
                <Tooltip title={note.is_pinned ? "Unpin" : "Pin"}>
                  <IconButton
                    size="small"
                    onClick={() => onTogglePin(note.id, note.is_pinned || false)}
                  >
                    {note.is_pinned ? <PushPin /> : <PushPin />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  const renderTimelineView = () => {
    const groupedNotes = filteredNotes.reduce((acc, note) => {
      const date = toDate(note.created_at);
      const dateKey = date ? formatDateSafe(date, 'yyyy-MM-dd') : 'unknown';
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(note);
      return acc;
    }, {} as Record<string, Note[]>);
    
    return (
      <Box sx={{ position: 'relative' }}>
        {Object.entries(groupedNotes).map(([dateKey, dateNotes], groupIndex) => (
          <Box key={dateKey} sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: 'primary.main',
                fontWeight: 'bold',
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                py: 1,
                zIndex: 1
              }}
            >
              {formatDateSafe(new Date(dateKey), 'EEEE, MMMM d, yyyy')}
            </Typography>
            
            {dateNotes.map((note, index) => (
              <Box key={note.id} sx={{ display: 'flex', mb: 3 }}>
                {/* Timeline Line */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 3 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: noteCategories.find(c => c.id === note.category)?.color || theme.palette.primary.main
                    }}
                  >
                    {noteCategories.find(c => c.id === note.category)?.icon || <NotesIcon />}
                  </Avatar>
                  {index < dateNotes.length - 1 && (
                    <Box
                      sx={{
                        width: 2,
                        flexGrow: 1,
                        backgroundColor: theme.palette.divider,
                        mt: 1
                      }}
                    />
                  )}
                </Box>
                
                {/* Note Content */}
                <Box sx={{ flexGrow: 1 }}>
                  {renderNoteCard(note, groupIndex * 10 + index)}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const renderCompactView = () => {
    return (
      <List>
        {filteredNotes.map((note, index) => {
          const category = noteCategories.find(c => c.id === note.category);
          
          return (
            <React.Fragment key={note.id}>
              <ListItem
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: note.is_pinned ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.05)
                  }
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {category && (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: alpha(category.color, 0.1),
                            color: category.color
                          }}
                        >
                          {category.icon}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: note.is_important ? 'bold' : 'normal' }}>
                          {note.content.substring(0, 100)}...
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {note.created_by_name} • {formatDistanceToNow(toDate(note.created_at) || new Date(), { addSuffix: true })}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Stack direction="row" spacing={0.5}>
                      {note.is_pinned && <PushPin sx={{ fontSize: 16, color: 'primary.main' }} />}
                      {note.is_important && <Star sx={{ fontSize: 16, color: 'warning.main' }} />}
                      {note.is_private && <Lock sx={{ fontSize: 16, color: 'text.secondary' }} />}
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, note)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>
              </ListItem>
              {index < filteredNotes.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Box sx={{ animation: `${fadeIn} 0.6s ease-out` }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: 3
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <NotesIcon sx={{ mr: 2, fontSize: 36 }} />
              Client Notes & Documentation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Comprehensive note-taking system for tracking client progress, insights, and important information
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Box>
                  <Typography variant="h3" color="primary">{statistics.total}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Total Notes</Typography>
                </Box>
                <Box>
                  <Typography variant="h3" color="secondary">{statistics.recentActivity}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">This Week</Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {noteCategories.map(category => {
          const count = statistics.categoryCounts[category.id] || 0;
          const percentage = statistics.total > 0 ? (count / statistics.total) * 100 : 0;
          
          return (
            <Grid item xs={6} sm={4} md={2} key={category.id}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: selectedCategory === category.id ? `2px solid ${category.color}` : '2px solid transparent',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  }
                }}
                onClick={() => setSelectedCategory(selectedCategory === category.id ? 'all' : category.id)}
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: alpha(category.color, 0.1),
                    color: category.color,
                    mx: 'auto',
                    mb: 1
                  }}
                >
                  {category.icon}
                </Avatar>
                <Typography variant="h6">{count}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {category.name}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{
                    mt: 1,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: alpha(category.color, 0.1),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: category.color
                    }
                  }}
                />
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Toolbar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          
          {/* View Mode */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="cards">
              <Tooltip title="Card View">
                <NotesIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="timeline">
              <Tooltip title="Timeline View">
                <Timeline />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="compact">
              <Tooltip title="Compact View">
                <FormatListBulleted />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          
          {/* Filters */}
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            size="small"
          >
            Filters {(selectedTags.length > 0 || showArchived || !showPrivate) && `(${selectedTags.length + (showArchived ? 1 : 0) + (!showPrivate ? 1 : 0)})`}
          </Button>
          
          {/* Sort */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              startAdornment={<Sort sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
              <MenuItem value="updated">Recently Updated</MenuItem>
            </Select>
          </FormControl>
          
          {/* Add Note */}
          {(userRole === 'MANAGER' || userRole === 'ADMIN' || userRole === 'USER') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenNoteDialog()}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                animation: `${shimmer} 3s ease-in-out infinite`
              }}
            >
              Add Note
            </Button>
          )}
        </Box>
        
        {/* Expandable Filters */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  options={allTags}
                  value={selectedTags}
                  onChange={(e, newValue) => setSelectedTags(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by tags"
                      size="small"
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                      />
                    }
                    label="Show Archived"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPrivate}
                        onChange={(e) => setShowPrivate(e.target.checked)}
                      />
                    }
                    label="Show Private"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Main Content */}
      {isLoading ? (
        <Box>
          {[1, 2, 3].map(i => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={200}
              sx={{ mb: 2, borderRadius: 2 }}
            />
          ))}
        </Box>
      ) : filteredNotes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <NotesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {searchQuery || selectedTags.length > 0 || selectedCategory !== 'all'
              ? 'No notes match your filters'
              : 'No notes yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchQuery || selectedTags.length > 0 || selectedCategory !== 'all'
              ? 'Try adjusting your search criteria'
              : 'Start documenting client progress and insights'}
          </Typography>
          {(userRole === 'MANAGER' || userRole === 'ADMIN' || 
            (userRole === 'USER' && (
              client?.lead_coach_id === currentUserId || 
              client?.collaborating_coach_ids?.includes(currentUserId)
            ))) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenNoteDialog()}
            >
              Add First Note
            </Button>
          )}
        </Paper>
      ) : (
        <Box>
          {viewMode === 'cards' && filteredNotes.map((note, index) => renderNoteCard(note, index))}
          {viewMode === 'timeline' && renderTimelineView()}
          {viewMode === 'compact' && renderCompactView()}
        </Box>
      )}

      {/* Note Dialog */}
      <Dialog
        open={showNoteDialog}
        onClose={() => setShowNoteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingNote ? 'Edit Note' : 'New Note'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Category and Color */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={noteCategory}
                    onChange={(e) => setNoteCategory(e.target.value)}
                    label="Category"
                  >
                    {noteCategories.map(cat => (
                      <MenuItem key={cat.id} value={cat.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {cat.icon}
                          {cat.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Note Color
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {noteColors.map(color => (
                      <IconButton
                        key={color.id}
                        onClick={() => setNoteColor(color.id)}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: color.color,
                          border: noteColor === color.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                          '&:hover': {
                            backgroundColor: color.color
                          }
                        }}
                      >
                        {noteColor === color.id && <Check sx={{ fontSize: 16 }} />}
                      </IconButton>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
            
            {/* Content */}
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Note Content"
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Enter your note here..."
              sx={{ mb: 3 }}
            />
            
            {/* Tags */}
            <Autocomplete
              multiple
              freeSolo
              options={allTags}
              value={noteTags}
              inputValue={tagInputValue}
              onChange={(e, newValue) => setNoteTags(newValue)}
              onInputChange={(event, newInputValue) => {
                setTagInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags..."
                  size="small"
                  sx={{ mb: 3 }}
                  onBlur={() => {
                    // If there's text in the input that's not already a tag, add it
                    if (tagInputValue.trim() && !noteTags.includes(tagInputValue.trim())) {
                      setNoteTags([...noteTags, tagInputValue.trim()]);
                      setTagInputValue(''); // Clear the input
                    }
                  }}
                  onKeyDown={(e) => {
                    // Also handle Enter key explicitly
                    if (e.key === 'Enter' && tagInputValue.trim() && !noteTags.includes(tagInputValue.trim())) {
                      e.preventDefault();
                      setNoteTags([...noteTags, tagInputValue.trim()]);
                      setTagInputValue(''); // Clear the input
                    }
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
            
            {/* Mentions */}
            <Autocomplete
              multiple
              options={coaches.map(c => c.name)}
              value={noteMentions}
              onChange={(e, newValue) => setNoteMentions(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mention Coaches"
                  placeholder="@mention..."
                  size="small"
                  sx={{ mb: 3 }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`@${option}`}
                    size="small"
                    color="primary"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
            
            {/* Options */}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noteIsPrivate}
                    onChange={(e) => setNoteIsPrivate(e.target.checked)}
                  />
                }
                label="Private Note (only visible to you)"
              />
              {(userRole === 'MANAGER' || userRole === 'ADMIN') && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={noteIsManagementOnly}
                      onChange={(e) => setNoteIsManagementOnly(e.target.checked)}
                      disabled={noteIsPrivate}
                    />
                  }
                  label="Management Only (visible to managers and admins only)"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noteIsImportant}
                    onChange={(e) => setNoteIsImportant(e.target.checked)}
                  />
                }
                label="Mark as Important"
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNoteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveNote}
            disabled={!noteContent.trim()}
          >
            {editingNote ? 'Update' : 'Add'} Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedNote) {
            handleOpenNoteDialog(selectedNote);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedNote) {
            onTogglePin(selectedNote.id, selectedNote.is_pinned || false);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <PushPin fontSize="small" />
          </ListItemIcon>
          <ListItemText>{selectedNote?.is_pinned ? 'Unpin' : 'Pin'}</ListItemText>
        </MenuItem>
        {onToggleArchive && (
          <MenuItem onClick={() => {
            if (selectedNote) {
              onToggleArchive(selectedNote.id, !selectedNote.is_archived);
            }
            handleMenuClose();
          }}>
            <ListItemIcon>
              {selectedNote?.is_archived ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{selectedNote?.is_archived ? 'Unarchive' : 'Archive'}</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => {
          if (selectedNote) {
            navigator.clipboard.writeText(selectedNote.content);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          // Share functionality
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Share fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          if (selectedNote) {
            onDeleteNote(selectedNote.id);
          }
          handleMenuClose();
        }} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ClientNotesView;